import { ButtonGroup, Checkbox, Classes, Colors, Dialog, Intent } from "@blueprintjs/core";
import { FieldSet, IDataTableColumn, showError, showSuccess, useGridState, useSearchApi } from "nsitools-react";
import * as React from "react";

import { DogWorkbookApi, DogWorkbookSearch, WorkbookApi, WorkbookGridDto } from "../../../../../api";
import { AddButton, CustomDataTable, ExportButton, PrintButton } from "../../../../../components";
import { useGlobalData } from "../../../../../contexts";
import { useApiService, useTl } from "../../../../../hooks";
import { ETLCodes } from "../../../../../locales";
import { DogWorkbookDialogPanel } from "./DogWorkbookDialogPanel";
import { ERoutes } from "../../../../../ERoutes";
import { Link } from "react-router-dom";
import { DogCurrentOwnerSelectionPanel } from "../DogDetails/People/DogCurrentOwnerSelectionPanel";

export interface IDogWorkbookListPageProps {
  dogId: number;
  editMode: boolean;
}

export const DogWorkbookListPage: React.FunctionComponent<IDogWorkbookListPageProps> = ({ dogId, editMode }) => {
  const { t } = useTl();
  const api = useApiService(DogWorkbookApi);
  const workbookApi = useApiService(WorkbookApi);
  const { currentLanguage } = useGlobalData();
  const [dialogOpened, setDialogOpened] = React.useState(false);
  const [sobjState, setsobjState] = React.useState<any>();
  const [reprintLoading, setreprintLoading] = React.useState<number[]>([]);

  const [showReprintDialog, setShowReprintDialog] = React.useState(false);
  const [selectedWorkbook, setselectedWorkbook] = React.useState<number>();

  const tableState = useGridState<any>({
    serverMode: true,
    enablePagination: true,
    enableFilter: true,
    availablePageSizes: [15, 25, 50],
    pageSize: 15,
    sortKeys: { deliveredOn: "DESC" },
  });

  const searchFunction = React.useCallback(
    (sObj?: DogWorkbookSearch) => {
      setsobjState(
        DogWorkbookSearch.fromJS({
          ...sObj,
          languageId: currentLanguage.languageId,
          dogId: dogId,
        })
      );
      return api.baseSearch(
        DogWorkbookSearch.fromJS({
          ...sObj,
          languageId: currentLanguage.languageId,
          dogId: dogId,
        })
      );
    },
    [api, currentLanguage.languageId, dogId]
  );

  const { search, loading } = useSearchApi<any, any>({
    searchFunction,
    tableState,
    initialSearch: true,
    trimSearchObjectStringValues: true,
  });

  const reprintWorkbook = React.useCallback(
    async (personId: number) => {
      reprintLoading.push(selectedWorkbook);
      setreprintLoading([...reprintLoading]);
      try {
        setShowReprintDialog(false);

        await workbookApi.reprintWorkbook(selectedWorkbook, personId);
        showSuccess(t(ETLCodes.PrintSuccess));
      } catch {
        showError(t(ETLCodes.Error));
      }
      setreprintLoading(reprintLoading.filter((c) => c !== selectedWorkbook));
      setselectedWorkbook(null);
    },
    [reprintLoading, selectedWorkbook, t, workbookApi]
  );

  const columns = React.useMemo<IDataTableColumn[]>(
    () => [
      {
        fieldName: "_action",
        computed: true,
        disableSorting: true,
        alignment: "center",
        autoFitContent: true,
        render: (row: WorkbookGridDto) => (
          <PrintButton
            onClick={() => {
              setselectedWorkbook(row.workbookId);
              setShowReprintDialog(true);
            }}
            loading={reprintLoading.find((c) => c === row.workbookId) > 0}
          />
        ),
      },
      {
        header: () => t(ETLCodes.Identifier),
        fieldName: "identifier",
        render: (row: WorkbookGridDto) => <div>{row.identifier > 0 ? row.identifier : ""}</div>,
      },
      {
        header: () => t(ETLCodes.Counter),
        fieldName: "counter",
        render: (row: WorkbookGridDto) => <div>{row.counter > 0 ? row.counter : ""}</div>,
      },
      {
        header: () => t(ETLCodes.WorkbookType),
        fieldName: "workbookType",
        render: (row: WorkbookGridDto) => (
          <Link target="_blank" to={`${ERoutes.workbookType}/${row.workbookTypeId}/detail/view`}>
            {row.workbookType}
          </Link>
        ),
      },
      {
        header: () => t(ETLCodes.WithPedigree),
        fieldName: "withPedigree",
        alignment: "center",
        autoFitContent: true,
        render: (row: WorkbookGridDto) => <Checkbox checked={row.withPedigree} disabled />,
      },
      {
        header: () => t(ETLCodes.DeliveredOn),
        fieldName: "deliveredOn",
      },
    ],
    [reprintLoading, t]
  );

  const addItemFunction = React.useCallback(() => {
    setDialogOpened(true);
  }, []);

  React.useEffect(() => {
    search();
  }, [currentLanguage, search]);

  return (
    <>
      <FieldSet
        title={t(ETLCodes.Workbooks)}
        rightElement={
          <ButtonGroup>
            <ExportButton exportFunction={(sObj) => api.exportXlsFile(sObj)} sObj={sobjState} />
            {editMode && (
              <AddButton
                onClick={(e) => {
                  e.stopPropagation();
                  addItemFunction();
                }}
                text={t(ETLCodes.General_Add)}
                intent={Intent.PRIMARY}
              />
            )}
          </ButtonGroup>
        }
      >
        <CustomDataTable tableState={tableState} loading={loading} columns={columns} />
      </FieldSet>
      <Dialog
        isOpen={showReprintDialog}
        title={t(ETLCodes.SelectContact)}
        className={""}
        canOutsideClickClose={true}
        style={{ width: 1200, background: Colors.WHITE }}
        onClose={() => {
          setShowReprintDialog(false);
          setselectedWorkbook(null);
        }}
      >
        <div className={Classes.DIALOG_BODY}>
          <DogCurrentOwnerSelectionPanel dogId={dogId} onPersonSelected={(personId) => reprintWorkbook(personId)} />
        </div>
      </Dialog>
      <Dialog
        title={t(ETLCodes.Workbook)}
        isOpen={dialogOpened}
        onClose={() => setDialogOpened(false)}
        canEscapeKeyClose={true}
        canOutsideClickClose={true}
        isCloseButtonShown={true}
        style={{ width: 1000, height: 330, background: Colors.WHITE }}
      >
        <div className={Classes.DIALOG_BODY} style={{ height: 200 }}>
          <DogWorkbookDialogPanel
            dogId={dogId}
            onSaved={() => {
              setDialogOpened(false);
              search();
            }}
          />
        </div>
      </Dialog>
    </>
  );
};
